import { useState, useEffect, useMemo } from 'react';
import {
  Newspaper,
  Plus,
  Trash2,
  Save,
  X,
  Edit3,
  Clock,
  RefreshCw,
  Globe,
  Tag,
  Tags
} from 'lucide-react';
import { COMMON_EVENTS, IMPORTANCE_LEVELS } from './economicCalendarConstants';
import {
  getEventsForDate,
  normalizeEvent,
  getCacheStatus,
  clearCache
} from './economicCalendarService';
import { getCurrentTradingDay } from '../../services/timeService';
import GlassSelect from '../Common/GlassSelect';

/**
 * EconomicEventsPanel - Displays and manages economic events for a trading day
 *
 * @param {Object} props
 * @param {boolean} props.darkMode - Current theme mode
 * @param {Array} props.newsEvents - Array of custom news events for the day (user-added)
 * @param {Function} props.onSaveEvents - Callback to save events (receives event array)
 * @param {boolean} props.readOnly - If true, hides edit controls
 * @param {Object} props.settings - Economic calendar settings (currencies, impactLevels, autoFetch)
 * @param {string} props.selectedDate - Currently selected date in YYYY-MM-DD format
 * @param {number} props.dayTradeCount - Number of trades logged on this day (for button gating)
 * @param {Function} props.onTagEventToAllTrades - Called with (event) to tag all day trades with that event
 * @param {Function} props.onTagAllEventsToAllTrades - Called to tag all day trades with every event
 */
const EconomicEventsPanel = ({
  darkMode,
  newsEvents = [],
  onSaveEvents,
  readOnly = false,
  settings = {},
  selectedDate = getCurrentTradingDay(),
  dayTradeCount = 0,
  onTagEventToAllTrades,
  onTagAllEventsToAllTrades,
  // Optional callback fired whenever the merged displayEvents (saved + API)
  // changes. Lets the parent (JournalView) auto-tag against the FULL visible
  // set instead of only the user-saved subset — without this, any API-only
  // events the trader sees in the panel are silently ignored by auto-tag.
  onDisplayEventsChange
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editEvents, setEditEvents] = useState([]);
  const [newEventTime, setNewEventTime] = useState('08:30');
  const [newEventName, setNewEventName] = useState('');
  const [newEventImportance, setNewEventImportance] = useState('medium');

  // API events state
  const [apiEvents, setApiEvents] = useState([]);
  const [isLoadingApi, setIsLoadingApi] = useState(false);
  const [apiError, setApiError] = useState(null);

  // Extract settings with defaults (no API key needed for FairEconomy.media)
  const {
    currencies = ['USD'],
    impactLevels = ['high', 'medium', 'low'],
    autoFetch = true
  } = settings;

  // Stringify settings for dependency tracking
  const currenciesKey = currencies.join(',');
  const impactLevelsKey = impactLevels.join(',');

  // Fetch API events when date changes or autoFetch is enabled
  useEffect(() => {
    const fetchApiEvents = async () => {
      if (!autoFetch) {
        setApiEvents([]);
        return;
      }

      setIsLoadingApi(true);
      setApiError(null);

      try {
        const events = await getEventsForDate(selectedDate, { currencies, impactLevels });
        const normalizedEvents = events.map(e => normalizeEvent(e));
        setApiEvents(normalizedEvents);
      } catch (error) {
        console.error('Error fetching economic events:', error);
        setApiError(error.message || 'Failed to fetch events');
        setApiEvents([]);
      } finally {
        setIsLoadingApi(false);
      }
    };

    fetchApiEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, autoFetch, currenciesKey, impactLevelsKey]);

  // Combine and filter events for display.
  // Deps use the stringified `currenciesKey`/`impactLevelsKey` instead of the
  // raw arrays — the parent destructures `currencies`/`impactLevels` fresh
  // from `settings` on every render, so the array identities churn even when
  // values don't change. The companion fetch effect above already uses the
  // stable keys; this memo had been silently re-running every parent render.
  const displayEvents = useMemo(() => {
    // Custom events (user-added) - these are always shown
    const customEvents = newsEvents.map(e => ({ ...e, isCustom: true }));

    // Filter API events by settings
    const filteredApiEvents = apiEvents.filter(event => {
      const currencyMatch = currencies.length === 0 || currencies.includes(event.currency);
      const impactMatch = impactLevels.length === 0 || impactLevels.includes(event.importance);
      return currencyMatch && impactMatch;
    });

    // Combine and sort by time
    const allEvents = [...customEvents, ...filteredApiEvents].sort((a, b) =>
      (a.time || '').localeCompare(b.time || '')
    );

    return allEvents;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newsEvents, apiEvents, currenciesKey, impactLevelsKey]);

  // Notify parent whenever the merged displayEvents changes — lets JournalView's
  // auto-tag effect work against API-fetched events too, not just user-saved ones.
  useEffect(() => {
    if (typeof onDisplayEventsChange === 'function') {
      onDisplayEventsChange(displayEvents);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayEvents]);

  // Manual refresh handler - uses forceRefresh to bypass cache in one request
  const handleRefresh = async () => {
    clearCache();
    setIsLoadingApi(true);
    setApiError(null);

    try {
      const events = await getEventsForDate(selectedDate, { currencies, impactLevels }, true);
      const normalizedEvents = events.map(normalizeEvent);
      setApiEvents(normalizedEvents);
    } catch (error) {
      console.error('Error refreshing economic events:', error);
      setApiError(error.message || 'Failed to refresh events');
    } finally {
      setIsLoadingApi(false);
    }
  };

  // Start editing mode
  const handleStartEdit = () => {
    setEditEvents([...newsEvents]);
    setIsEditing(true);
  };

  // Save changes
  const handleSave = () => {
    onSaveEvents(editEvents);
    setIsEditing(false);
  };

  // Cancel editing
  const handleCancel = () => {
    setIsEditing(false);
    setEditEvents([]);
  };

  // Add new event
  const handleAddEvent = () => {
    if (!newEventName.trim()) return;

    const newEvent = {
      // Random suffix avoids collisions when user adds two events in <1ms
      // (e.g., rapid keyboard-Enter spam in the inline editor).
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      time: newEventTime,
      name: newEventName.trim(),
      importance: newEventImportance,
      isCustom: true
    };

    // Add and sort by time
    const updated = [...editEvents, newEvent].sort((a, b) =>
      a.time.localeCompare(b.time)
    );

    setEditEvents(updated);
    setNewEventName('');
    setNewEventTime('08:30');
    setNewEventImportance('medium');
  };

  // Quick add a common event
  const handleQuickAdd = (event) => {
    const newEvent = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      time: newEventTime,
      name: event.name,
      importance: event.importance,
      isCustom: true
    };

    const updated = [...editEvents, newEvent].sort((a, b) =>
      a.time.localeCompare(b.time)
    );

    setEditEvents(updated);
  };

  // Remove event
  const handleRemoveEvent = (eventId) => {
    setEditEvents(editEvents.filter(e => e.id !== eventId));
  };

  // Get importance indicator color class
  const getImportanceClass = (importance) => {
    return IMPORTANCE_LEVELS[importance]?.bgClass || 'bg-gray-500';
  };

  // Get cache status for display
  const cacheStatus = getCacheStatus();

  return (
    <div className={`p-4 rounded-lg border ${
      darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-white border-gray-200'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Newspaper size={18} className="text-orange-500" />
          <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            News & Economic Events
          </h3>
          {/* Importance Legend */}
          <div className="flex items-center gap-2 ml-3 text-xs">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-red-500"></span>High
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-yellow-500"></span>Med
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-blue-500"></span>Low
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Cache status indicator */}
          {cacheStatus.hasCachedData && (
            <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} title={`${cacheStatus.eventCount} events cached`}>
              {cacheStatus.minutesOld}m ago
            </span>
          )}
          
          {/* Refresh button - always available */}
          <button
            onClick={handleRefresh}
            disabled={isLoadingApi}
            className={`p-1.5 rounded transition-colors ${
              isLoadingApi
                ? 'animate-spin text-blue-500'
                : darkMode
                  ? 'text-gray-400 hover:bg-gray-600'
                  : 'text-gray-500 hover:bg-gray-100'
            }`}
            title={cacheStatus.hasCachedData
              ? `Last updated: ${cacheStatus.timestamp} - Click to refresh`
              : 'Fetch events from API'}
          >
            <RefreshCw size={16} />
          </button>

          {!readOnly && (
            !isEditing ? (
              <button
                onClick={handleStartEdit}
                className={`p-1.5 rounded hover:bg-blue-500 hover:text-white transition-colors ${
                  darkMode ? 'text-gray-400' : 'text-gray-500'
                }`}
                title="Edit custom events"
              >
                <Edit3 size={16} />
              </button>
            ) : (
              <div className="flex gap-1">
                <button
                  onClick={handleSave}
                  className="p-1.5 rounded-sm bg-green-500 text-white hover:bg-green-600 transition-colors"
                >
                  <Save size={16} />
                </button>
                <button
                  onClick={handleCancel}
                  className={`p-1.5 rounded transition-colors ${
                    darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <X size={16} />
                </button>
              </div>
            )
          )}
        </div>
      </div>

      {/* Error Display */}
      {apiError && (
        <div className={`text-xs mb-2 ${darkMode ? 'text-red-400' : 'text-red-500'}`}>
          {apiError}
        </div>
      )}

      {/* Edit Mode */}
      {isEditing ? (
        <div className="space-y-3">
          {/* Add New Event Form */}
          <div className={`p-3 rounded-lg border ${
            darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex flex-wrap items-end gap-2">
              {/* Time Input */}
              <div className="shrink-0">
                <label className={`block text-xs mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Time (ET)
                </label>
                <input
                  type="time"
                  value={newEventTime}
                  onChange={(e) => setNewEventTime(e.target.value)}
                  className="input-modern w-32"
                />
              </div>

              {/* Event Name Input */}
              <div className="grow min-w-[200px]">
                <label className={`block text-xs mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Event Name
                </label>
                <input
                  type="text"
                  value={newEventName}
                  onChange={(e) => setNewEventName(e.target.value)}
                  placeholder="e.g., CPI Report, FOMC Minutes..."
                  className="w-full input-modern"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newEventName.trim()) {
                      handleAddEvent();
                    }
                  }}
                />
              </div>

              {/* Importance Select */}
              <div className="shrink-0">
                <label className={`block text-xs mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Impact
                </label>
                <GlassSelect
                  value={newEventImportance}
                  onChange={(v) => setNewEventImportance(v)}
                  options={[
                    { value: 'high', label: '🔴 High' },
                    { value: 'medium', label: '🟡 Medium' },
                    { value: 'low', label: '🔵 Low' },
                  ]}
                  darkMode={darkMode}
                  wrapperClassName="relative inline-block"
                  className="w-32"
                />
              </div>

              {/* Add Button */}
              <button
                onClick={handleAddEvent}
                disabled={!newEventName.trim()}
                className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                  newEventName.trim()
                    ? 'bg-blue-500 text-white hover:bg-blue-600'
                    : darkMode
                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                <Plus size={16} />
              </button>
            </div>

            {/* Quick Add Buttons */}
            <div className="mt-2 flex flex-wrap gap-1">
              <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Quick add:
              </span>
              {COMMON_EVENTS.slice(0, 6).map(event => (
                <button
                  key={event.name}
                  onClick={() => handleQuickAdd(event)}
                  className={`px-2 py-0.5 rounded text-xs transition-colors ${
                    darkMode
                      ? 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {event.name.split(' ')[0]}
                </button>
              ))}
            </div>
          </div>

          {/* Events List (Editing) - Only shows custom events */}
          {editEvents.length > 0 ? (
            <div className="space-y-1">
              <div className={`text-xs mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Custom Events (editable):
              </div>
              {editEvents.map((event) => (
                <div
                  key={event.id}
                  className={`flex items-center gap-3 p-2 rounded ${
                    darkMode ? 'bg-gray-700/30' : 'bg-gray-50'
                  }`}
                >
                  <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                    getImportanceClass(event.importance)
                  }`}></span>
                  <span className={`text-sm font-mono w-16 shrink-0 ${
                    darkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    {event.time}
                  </span>
                  <span className={`grow text-sm text-left ${
                    darkMode ? 'text-gray-200' : 'text-gray-800'
                  }`}>
                    {event.name}
                  </span>
                  <button
                    onClick={() => handleRemoveEvent(event.id)}
                    className={`p-1 rounded hover:bg-red-500 hover:text-white transition-colors shrink-0 ${
                      darkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className={`text-sm italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              No custom events added yet. Use the form above to add economic events.
            </p>
          )}
        </div>
      ) : (
        /* Display Mode */
        <div>
          {isLoadingApi && (
            <div className={`text-xs mb-2 flex items-center gap-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <RefreshCw size={12} className="animate-spin" />
              Loading events...
            </div>
          )}

          {displayEvents && displayEvents.length > 0 ? (
            <div>
              {/* Master "Tag all today's trades" button — appears above the list.
                  Pass the FULL displayEvents set (includes API-fetched events
                  + user-saved customs) so the handler can tag against all
                  currently-visible events, not just the saved subset. */}
              {onTagAllEventsToAllTrades && dayTradeCount > 0 && displayEvents.length > 1 && (
                <div className="flex justify-end mb-2">
                  <button
                    onClick={() => onTagAllEventsToAllTrades(displayEvents)}
                    className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition-colors ${
                      darkMode
                        ? 'bg-purple-900/40 text-purple-300 hover:bg-purple-900/60 border border-purple-700/40'
                        : 'bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200'
                    }`}
                    title={`Tag all ${dayTradeCount} trade${dayTradeCount === 1 ? '' : 's'} today with every event listed`}
                  >
                    <Tags size={12} />
                    Tag all trades today
                  </button>
                </div>
              )}

              <div className="space-y-1">
                {displayEvents.map((event, idx) => (
                  <div key={event.id || idx} className="flex items-center gap-3 py-1">
                    <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                      getImportanceClass(event.importance)
                    }`}></span>
                    <Clock size={12} className={`shrink-0 ${
                      darkMode ? 'text-gray-500' : 'text-gray-400'
                    }`} />
                    <span className={`text-sm font-mono w-16 shrink-0 ${
                      darkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {event.time}
                    </span>
                    <span className={`text-sm text-left grow ${
                      darkMode ? 'text-gray-200' : 'text-gray-800'
                    }`}>
                      {event.name}
                      {/* Show Forecast / Actual inline when available */}
                      {(event.forecast || event.actual) && (
                        <span className={`ml-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {event.forecast && `Forecast: ${event.forecast}`}
                          {event.forecast && event.actual && ' / '}
                          {event.actual && (
                            <span className={
                              event.forecast && event.actual
                                ? parseFloat(event.actual) > parseFloat(event.forecast)
                                  ? 'text-green-400'
                                  : parseFloat(event.actual) < parseFloat(event.forecast)
                                    ? 'text-red-400'
                                    : ''
                                : ''
                            }>
                              Actual: {event.actual}
                            </span>
                          )}
                        </span>
                      )}
                    </span>
                    {/* Per-event "Tag all trades today" button */}
                    {onTagEventToAllTrades && dayTradeCount > 0 && (
                      <button
                        onClick={() => onTagEventToAllTrades(event)}
                        className={`shrink-0 p-1 rounded transition-colors ${
                          darkMode
                            ? 'text-gray-400 hover:bg-purple-900/60 hover:text-purple-300'
                            : 'text-gray-500 hover:bg-purple-100 hover:text-purple-700'
                        }`}
                        title={`Tag all ${dayTradeCount} trade${dayTradeCount === 1 ? '' : 's'} today with "${event.name}"`}
                      >
                        <Tag size={12} />
                      </button>
                    )}
                    {/* Show source indicator */}
                    {event.isFromApi && (
                      <Globe
                        size={12}
                        className={`shrink-0 ${darkMode ? 'text-blue-400' : 'text-blue-500'}`}
                        title={`API: ${event.currency || 'USD'}`}
                      />
                    )}
                    {event.currency && !event.isCustom && (
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'
                      }`}>
                        {event.currency}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className={`text-sm italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {autoFetch
                ? 'No economic events found for this day.'
                : 'Auto-fetch disabled. Click refresh ↻ to fetch events, or add custom events.'}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default EconomicEventsPanel;
